import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Instagram } from "lucide-react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { RevealLine, ScrollReveal } from "@/components/Reveal";

const DRIVE = "https://lh3.googleusercontent.com/d/";
const FROG_IMG = `${DRIVE}1D-tMtlWXOMGqRD1a7wep4VA489GNbBk5=w2000`;
const VIREO_IMG = `${DRIVE}1DochN-b5BG591w6yY56juCZvSstaWvlZ=w2000`;

const CLUBS = [
  {
    name: "CK Bee Club",
    text: "Come join us to learn more about bees and their environmental impact! We beekeep, plant native foliage to add to CK's biodiversity, educate at Brookside and the middle schools, and work to better our community's environment!",
    image: `${DRIVE}1-bLa-pGCAJwvoHMsFd2JeGy_J1VEyKTA=w2000`,
    alt: "Honeybees on a frame from the Bee Club's hives",
  },
  {
    name: "LITE Club",
    text: "Make friends, work for real change in our community, and raise awareness about climate change. We have meetings once a month and annual events, including flower sales, Miles4Miles, and trash cleanups!",
    image: `${DRIVE}1DIybUvUyZ1FDnkPrX0GUO9q67o_MwfE4=w2000`,
    alt: "A green heron with a freshly caught fish on campus",
  },
  {
    name: "CK Garden Club",
    text: "Plant, grow, harvest and learn new skills in the school's garden! An activity credit that's low stress. Our goal is to educate members about growing and harvesting fruits and vegetables while giving to communities that may not have access to healthy goods.",
    image: `${DRIVE}1AQZAp2-AwwiEZA0HI4zwWMzUtunth1JU=w2000`,
    alt: "Fresh plants growing in the school garden",
  },
  {
    name: "Biology Club",
    text: "Go beyond the textbook with fun experiments and learn more about biology through weekly presentations and projects! Along with biology tutoring and available competitions, get involved in citizen science to help solve real world problems!",
    image: `${DRIVE}15Ea8a8GEXfMWAhd-HSxy3bSozOk3eprM=w2000`,
    alt: "A bird box built by the Biology Club",
  },
];

const ClubStrip = ({ club, flip, index }) => (
  <section className="group relative flex min-h-[440px] items-center overflow-hidden" data-testid={`club-strip-${index}`}>
    <img
      src={club.image}
      alt={club.alt}
      loading="lazy"
      className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-[1.04]"
    />
    <div className="absolute inset-0 bg-black/20" />
    <div className={`relative mx-auto w-full max-w-7xl px-6 md:px-16 ${flip ? "md:text-right" : ""}`}>
      <ScrollReveal className={flip ? "md:ml-auto" : ""}>
        <div className="max-w-md border border-border bg-background/95 p-8 shadow-[0_24px_70px_-30px_rgba(20,40,25,0.4)] backdrop-blur-sm md:p-10">
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-primary">Club {String(index + 1).padStart(2, "0")}</p>
          <h3 className="mt-3 font-serif text-3xl font-semibold tracking-tight md:text-4xl" data-testid={`club-name-${index}`}>
            {club.name}
          </h3>
          <p className="mt-4 text-sm leading-relaxed text-foreground/80">{club.text}</p>
          <a
            href="https://clubhub.co"
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`club-join-${index}`}
            className="group/btn mt-6 inline-flex items-center gap-2 border border-foreground px-5 py-3 text-xs font-bold uppercase tracking-[0.2em] transition-colors duration-300 hover:bg-foreground hover:text-background"
          >
            Join us via Clubhub
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
          </a>
        </div>
      </ScrollReveal>
    </div>
  </section>
);

export default function Clubs() {
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 700], [0, 180]);

  return (
    <div className="min-h-screen" data-testid="clubs-page">
      <Navbar />

      {/* Frog banner hero */}
      <section className="relative flex h-[62vh] items-center justify-center overflow-hidden" data-testid="clubs-hero">
        <motion.div style={{ y: heroY }} className="absolute inset-0">
          <img
            src={FROG_IMG}
            srcSet="https://lh3.googleusercontent.com/d/1D-tMtlWXOMGqRD1a7wep4VA489GNbBk5=w900 900w, https://lh3.googleusercontent.com/d/1D-tMtlWXOMGqRD1a7wep4VA489GNbBk5=w1600 1600w, https://lh3.googleusercontent.com/d/1D-tMtlWXOMGqRD1a7wep4VA489GNbBk5=w2000 2000w"
            sizes="100vw"
            alt="A green frog peeking through duckweed on a campus pond"
            className="h-[125%] w-full object-cover"
          />
          <div className="absolute inset-0 bg-black/35" />
        </motion.div>
        <h1
          className="relative text-center font-serif text-[13vw] font-semibold leading-none tracking-tighter text-[#f6f3ea] md:text-[8vw]"
          data-testid="clubs-title"
        >
          <RevealLine delay={0.2}>Clubs &amp; Wildlife</RevealLine>
        </h1>
      </section>

      {/* Club strips */}
      <div className="py-16 md:py-24" data-testid="clubs-list">
        <ScrollReveal className="mx-auto mb-16 max-w-3xl px-6 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.35em] text-primary">Find your people</p>
          <h2 className="mt-4 font-serif text-4xl font-semibold tracking-tight md:text-5xl">Four clubs keeping campus wild</h2>
        </ScrollReveal>
        <div className="space-y-10">
          {CLUBS.map((club, i) => (
            <ClubStrip key={club.name} club={club} flip={i % 2 === 1} index={i} />
          ))}
        </div>
      </div>

      {/* Wildlife on Campus */}
      <section className="mx-auto max-w-3xl px-6 py-24 text-center md:py-32" data-testid="wildlife-section">
        <ScrollReveal>
          <h2 className="font-serif text-4xl font-semibold tracking-tight md:text-6xl">Wildlife on Campus</h2>
          <p className="mx-auto mt-8 max-w-2xl text-base leading-loose text-foreground/80 md:text-lg">
            CK campus is home to many species of birds, insects, mammals, fish, reptiles, and amphibians! Explore some of the photography of these species, educational videos, and favorite stories from on campus through the link below!
          </p>
        </ScrollReveal>
      </section>

      {/* Vireo CTA */}
      <section className="group relative flex min-h-[420px] items-center justify-center overflow-hidden" data-testid="wildlife-cta">
        <img
          src={VIREO_IMG}
          alt="A red-eyed vireo perched in campus greenery"
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-[1.04]"
        />
        <div className="absolute inset-0 bg-black/30" />
        <ScrollReveal className="relative">
          <Link
            to="/#gallery"
            data-testid="wildlife-gallery-link"
            className="block border border-border bg-background/95 px-10 py-8 text-center shadow-[0_24px_70px_-30px_rgba(20,40,25,0.45)] backdrop-blur-sm transition-transform duration-500 hover:-translate-y-2 md:px-14"
          >
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-primary">Right here on this site</p>
            <p className="mt-3 flex items-center gap-2 font-serif text-2xl font-semibold tracking-tight md:text-3xl">
              Check out photography and educational content here
              <ArrowUpRight className="h-6 w-6 text-primary" />
            </p>
          </Link>
        </ScrollReveal>
      </section>

      {/* Links strip */}
      <footer className="border-t border-border bg-foreground text-background" data-testid="clubs-footer">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-6 px-6 py-10 md:px-16">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-background/50">Links</p>
          <a
            href="https://instagram.com/ck_findingfauna"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="clubs-instagram-link"
            className="flex items-center gap-2 text-sm transition-colors duration-300 hover:text-primary"
          >
            <Instagram className="h-4 w-4" />
            @ck_findingfauna
          </a>
        </div>
      </footer>
    </div>
  );
}
